// frontend/src/components/auth/ForgotPasswordForm.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { authAPI } from '../../services/api';
import AuthLayout from './AuthLayout';

export const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      // Show success anyway for security (don't reveal if email exists)
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout title="Check your email">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success-tint mb-4">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <div className="space-y-2 text-body text-ink-muted">
            <p>
              If an account exists with <strong className="text-ink-soft">{email}</strong>, you will receive a password reset link shortly.
            </p>
            <p>Please check your inbox and spam folder.</p>
            <p className="text-small mt-4">
              The link will expire in 1 hour for security reasons.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <button
            onClick={() => navigate('/login')}
            className="w-full min-h-touch flex items-center justify-center gap-2 rounded-lg
              bg-white text-ink-soft border border-border-default font-medium text-body
              hover:bg-surface-muted transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
          <button
            onClick={() => { setSuccess(false); setEmail(''); }}
            className="w-full text-body text-accent hover:text-accent-hover transition-colors"
          >
            Try a different email
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="Enter your email and we'll send you a reset link."
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <label htmlFor="email" className="block text-small font-semibold text-ink-soft">
            Email address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              className={`w-full min-h-touch pl-10 pr-3 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${error ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Enter your email"
            />
          </div>
          {error && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <button
            type="submit"
            disabled={loading}
            className="w-full min-h-touch flex items-center justify-center rounded-lg
              bg-accent text-white font-medium text-body
              hover:bg-accent-hover transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              'Send reset link'
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full min-h-touch flex items-center justify-center gap-2 rounded-lg
              bg-white text-ink-soft border border-border-default font-medium text-body
              hover:bg-surface-muted transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
        </div>

        <p className="text-center text-body text-ink-muted">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="text-accent hover:text-accent-hover font-medium transition-colors"
          >
            Sign up
          </button>
        </p>
      </form>
    </AuthLayout>
  );
};
